/**
 * Admin-only page listing recent security scan runs.
 *
 * Reads through the SECURITY DEFINER `admin_list_security_scan_runs` RPC
 * (super_admin only) and offers a "failures only" filter so denied/failed
 * nightly runs are easy to spot without paging the whole history.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, RefreshCw, ShieldCheck, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { AdminFindingHistory } from "@/components/AdminFindingHistory";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/security-runs")({
  component: SecurityRunsPage,
  head: () => ({
    meta: [
      { title: "Security Scan Runs — Inquira Admin" },
      {
        name: "description",
        content:
          "Admin-only history of Inquira security scan runs with timestamp, source, status, HTTP status and denial reason.",
      },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:type", content: "website" },
      { property: "og:title", content: "Security Scan Runs — Inquira Admin" },
      {
        property: "og:description",
        content: "Admin-only history of Inquira security scan runs and failures.",
      },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Security Scan Runs — Inquira Admin" },
      {
        name: "twitter:description",
        content: "Admin-only history of Inquira security scan runs and failures.",
      },
    ],
  }),
});

interface ScanRun {
  id: string;
  source: string;
  status: string;
  http_status: number | null;
  reason: string | null;
  detail: string | null;
  hint: string | null;
  actor_email: string | null;
  findings_count: number | null;
  created_at: string;
}

const FAILURE_STATUSES = new Set(["denied", "failed", "error"]);

function isFailure(run: ScanRun) {
  return FAILURE_STATUSES.has(run.status);
}

function SecurityRunsPage() {
  const { user, isLoading: authLoading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [checking, setChecking] = useState(true);
  const [rows, setRows] = useState<ScanRun[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [failuresOnly, setFailuresOnly] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate({ to: "/login" });
      return;
    }
    (async () => {
      const { data: roleOk } = await supabase.rpc("has_role" as never, {
        _user_id: user.id,
        _role: "super_admin",
      } as never);
      setIsAdmin(roleOk === true);
      setChecking(false);
    })();
  }, [authLoading, user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc("admin_list_security_scan_runs" as never, {
      _limit: 100,
      _offset: 0,
    } as never);
    setLoading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    const payload = data as unknown as { rows: ScanRun[]; total: number } | null;
    setRows(payload?.rows ?? []);
    setTotal(payload?.total ?? 0);
  }, []);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  if (authLoading || checking) {
    return <p className="p-6 text-sm text-muted-foreground">{t("common.loading")}</p>;
  }

  if (!isAdmin) {
    return (
      <main className="mx-auto max-w-xl p-6">
        <h1 className="text-xl font-bold">{t("adminScreens.notAvailable")}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("adminScreens.adminsOnly")}
        </p>
        <Link to="/" className="mt-4 inline-flex items-center gap-2 text-sm underline">
          <ArrowLeft className="h-4 w-4" /> {t("adminScreens.backHome")}
        </Link>
      </main>
    );
  }

  const visible = failuresOnly ? rows.filter(isFailure) : rows;
  const failureCount = rows.filter(isFailure).length;

  return (
    <main className="mx-auto max-w-5xl p-4 sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-xl font-bold">
          <ShieldCheck className="h-5 w-5 text-primary" /> {t("adminScreens.securityScanRuns")}
        </h1>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={failuresOnly}
              onChange={(e) => setFailuresOnly(e.target.checked)}
              aria-label={t("adminScreens.failuresOnlyLabel")}
            />
            {t("adminScreens.failuresOnly", { count: failureCount })}
          </label>
          <button
            onClick={() => void load()}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs disabled:opacity-60"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> {t("adminScreens.refresh")}
          </button>
        </div>
      </div>

      <p className="mb-3 text-xs text-muted-foreground">
        {t("adminScreens.runsRecorded", { count: total })}
      </p>

      {visible.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {loading ? t("common.loading") : t("adminScreens.noMatchingRuns")}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border">
          <table className="w-full text-xs" data-testid="security-runs-table">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="p-2">{t("adminScreens.timestamp")}</th>
                <th className="p-2">{t("adminScreens.source")}</th>
                <th className="p-2">{t("adminScreens.status")}</th>
                <th className="p-2">HTTP</th>
                <th className="p-2">{t("adminScreens.reason")}</th>
                <th className="p-2">{t("adminScreens.findings")}</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const bad = isFailure(r);
                return (
                  <tr key={r.id} className="border-t border-border align-top">
                    <td className="p-2 whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString()}
                    </td>
                    <td className="p-2">{r.source}</td>
                    <td className="p-2">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-semibold ${
                          bad ? "bg-destructive/15 text-destructive" : "bg-primary/15 text-primary"
                        }`}
                      >
                        {bad && <AlertTriangle className="h-3 w-3" />}
                        {r.status}
                      </span>
                    </td>
                    <td className="p-2">{r.http_status ?? "—"}</td>
                    <td className="p-2">
                      {r.reason ?? "—"}
                      {r.detail && (
                        <span className="block text-muted-foreground">{r.detail}</span>
                      )}
                    </td>
                    <td className="p-2">{r.findings_count ?? "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <AdminFindingHistory />

      <Link to="/admin" className="mt-4 inline-flex items-center gap-2 text-sm underline">
        <ArrowLeft className="h-4 w-4" /> {t("adminScreens.backToAdmin")}
      </Link>
    </main>
  );
}
